/**
 * AutoAgentPane — the AI Agent UI extracted from AutoAgentPage so it can
 * also be embedded inside Builder Mode's workspace (left pane).
 *
 * This is the proven, working implementation: same hook (useAutoAgent),
 * same lifecycle (init → start → todo stream → chat → questions). The
 * outer route (AutoAgentPage) is now a thin wrapper that just reads URL
 * params and renders this pane.
 *
 * Props:
 *   - project: required project folder name. The pane creates / reuses
 *     an auto-agent session bound to this project.
 *   - initialDescription: optional. When present and there's no active
 *     session to reconnect to, the pane auto-fires `start()` after a
 *     short delay so the WS is ready.
 *   - onComplete: optional callback when the pipeline finishes.
 *   - showBackButton: whether to render the back-arrow in the header
 *     (true for the standalone route, false when embedded).
 *   - compact: trims header/sidebar widths a notch when embedded inside
 *     a split pane.
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import {
  Play, Pause, StopCircle, RotateCcw, Send, CheckCircle2, XCircle,
  Loader2, Clock, AlertTriangle, ChevronRight, Sparkles, SkipForward,
  Trash2, ArrowLeft, Bot, Timer, ListChecks,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAutoAgent, type AutoAgentTodo, type TodoStatus } from '@/hooks/useAutoAgent'
import AgentAskBubble from '@/components/agent/AgentAskBubble'
import ScopeConfirmation from '@/components/agent/ScopeConfirmation'
import SourceTablePicker from '@/components/agent/SourceTablePicker'
import { listDeploymentProfilesForAgent, clearAutoAgentState, type DeploymentProfile } from '@/api/autoagent'
import { getSettings } from '@/api/settings'
import { modelsForAgent, type AgentId } from '@/data/llmCatalog'
import { AGENT_DISPLAY_NAME } from '@/data/agentBranding'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { formatSpend } from '@/store/creditsStore'

const STATUS_ICON: Record<TodoStatus, ReactNode> = {
  pending: <Clock size={13} style={{ color: 'var(--color-text-secondary)', opacity: 0.4 }} />,
  running: <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />,
  completed: <CheckCircle2 size={13} style={{ color: 'var(--color-success, #1a7f37)' }} />,
  skipped: <SkipForward size={13} style={{ color: 'var(--color-text-secondary)', opacity: 0.4 }} />,
  error: <XCircle size={13} style={{ color: 'var(--color-error, #cf222e)' }} />,
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const min = Math.floor(seconds / 60)
  const sec = Math.round(seconds % 60)
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`
}

function formatTime(epoch: number): string {
  return new Date(epoch * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function TodoItem({ todo }: { todo: AutoAgentTodo }) {
  const duration =
    todo.started_at && todo.completed_at
      ? todo.completed_at - todo.started_at
      : null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.5rem',
        padding: '0.5rem 0.625rem',
        borderRadius: 'var(--radius-md)',
        background: todo.status === 'running'
          ? 'rgba(76,141,255,0.06)'
          : todo.status === 'error'
          ? 'rgba(244,67,54,0.04)'
          : 'transparent',
        opacity: todo.status === 'skipped' ? 0.45 : 1,
        transition: 'background 0.2s',
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 2 }}>{STATUS_ICON[todo.status]}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.875rem',
          color: todo.status === 'running' ? 'var(--color-text)' : 'var(--color-text-secondary)',
          fontWeight: todo.status === 'running' ? 600 : 400,
          lineHeight: 1.4,
        }}>
          {todo.label}
        </div>
        {todo.error && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)', marginTop: 2, lineHeight: 1.3 }}>
            {todo.error}
          </div>
        )}
        {todo.started_at && todo.status !== 'pending' && todo.status !== 'running' && (
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: 3,
            fontSize: '0.8125rem',
            color: 'var(--color-text-secondary)',
            opacity: 0.7,
          }}>
            {todo.completed_at && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <Clock size={10} />
                {formatTime(todo.completed_at)}
              </span>
            )}
            {duration !== null && duration > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <Timer size={10} />
                {formatDuration(duration)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function InfoChip({ label, value }: { label: string; value: string }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: '0.8125rem',
      padding: '2px 8px',
      borderRadius: 'var(--radius-full)',
      background: 'var(--color-bg)',
      color: 'var(--color-text-secondary)',
      border: '1px solid var(--color-border)',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ opacity: 0.6 }}>{label}:</span>
      <span style={{ fontWeight: 500, color: 'var(--color-text)' }}>{value}</span>
    </span>
  )
}

export interface AutoAgentPaneProps {
  project: string
  initialDescription?: string
  // Mode for the auto-start when initialDescription is present. 'build' runs
  // the agentic multi-agent orchestrator (the "AI Agent" entry); 'step' runs
  // the deterministic FRS -> review -> app pipeline.
  initialMode?: 'step' | 'build'
  onComplete?: () => void
  showBackButton?: boolean
  compact?: boolean
}

export default function AutoAgentPane({
  project,
  initialDescription = '',
  initialMode = 'step',
  onComplete,
  showBackButton = false,
  compact = false,
}: AutoAgentPaneProps) {
  const navigate = useNavigate()

  const [description, setDescription] = useState(initialDescription)
  const [profiles, setProfiles] = useState<DeploymentProfile[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)
  const [loadingProfiles, setLoadingProfiles] = useState(true)
  const [chatInput, setChatInput] = useState('')
  const [chatModel, setChatModel] = useState<string>('')
  const [started, setStarted] = useState(false)
  const [showSetup, setShowSetup] = useState(true)

  const [generationMode, setGenerationMode] = useState<string>('llm')
  const [llmProvider, setLlmProvider] = useState<string>('')
  const [languageModel, setLanguageModel] = useState<string>('')
  const [claudeModel, setClaudeModel] = useState<string>('')
  const [thinkingEffort, setThinkingEffort] = useState<string>('')

  const logEndRef = useRef<HTMLDivElement>(null)

  const agent = useAutoAgent({
    project,
    onComplete: () => {
      toast.success('Application generated successfully!')
      onComplete?.()
    },
  })

  // Auto-scroll logs
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [agent.logs, agent.replies])

  // Settings
  useEffect(() => {
    getSettings().then((s) => {
      // Use the EFFECTIVE mode (accounts for the platform default a new user
      // rides) so the badge reflects what Build will actually run, not the raw
      // legacy generation_mode field which defaults to 'llm'.
      setGenerationMode(s.effective_generation_mode || s.generation_mode || 'llm')
      setLlmProvider(s.llm_provider || '')
      setLanguageModel(s.language_model || '')
      setClaudeModel(s.claude_model || '')
      const effort = s.thinking_effort_config || {}
      setThinkingEffort((effort as Record<string, string>).model || '')
    }).catch(() => {})
  }, [])

  // Deployment profiles
  useEffect(() => {
    setLoadingProfiles(true)
    listDeploymentProfilesForAgent()
      .then((p) => {
        setProfiles(p)
        const defaultProfile = p.find((x) => x.is_default)
        if (defaultProfile) setSelectedProfileId(defaultProfile.id)
      })
      .catch(() => {})
      .finally(() => setLoadingProfiles(false))
  }, [])

  const autoStartedRef = useRef(false)

  // Reflect server-side status on reconnect
  useEffect(() => {
    if (['running', 'paused', 'completed'].includes(agent.agentStatus)) {
      setStarted(true)
      setShowSetup(false)
    }
  }, [agent.agentStatus])

  // Init + optional auto-start when an initialDescription is provided.
  // 1500 ms delay mirrors AutoAgentPage — gives the WS time to fully open
  // before we send `start`.
  useEffect(() => {
    let cancelled = false
    agent.initSession().then((result) => {
      if (!result || cancelled) return
      if (result.reconnected) return
      if (initialDescription && !autoStartedRef.current) {
        autoStartedRef.current = true
        const waitAndStart = async () => {
          await new Promise<void>((r) => setTimeout(r, 1500))
          if (!cancelled) {
            setStarted(true)
            setShowSetup(false)
            agent.start(initialDescription, selectedProfileId, initialMode)
          }
        }
        waitAndStart()
      }
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project])

  const handleStart = useCallback(async (mode: 'step' | 'build' = 'step') => {
    if (!description.trim()) {
      toast.error('Please describe the application to generate')
      return
    }
    setStarted(true)
    setShowSetup(false)
    await agent.start(description, selectedProfileId, mode)
  }, [description, selectedProfileId, agent])

  const handleClearAndRestart = useCallback(async () => {
    if (!agent.sessionId) return
    await clearAutoAgentState(agent.sessionId)
    agent.clearLogs()
    setStarted(false)
    setShowSetup(true)
    toast.success('State cleared — ready to start fresh')
  }, [agent])

  const handleSendMessage = useCallback(() => {
    if (!chatInput.trim()) return
    agent.sendMessage(chatInput, chatModel || undefined)
    setChatInput('')
  }, [chatInput, chatModel, agent])

  const handleAnswerQuestion = useCallback((answer: string, connectionId?: string) => {
    if (!agent.question) return
    agent.answerQuestion(agent.question.question_id, answer, connectionId)
  }, [agent])

  const isRunning = agent.agentStatus === 'running'
  const isPaused = agent.agentStatus === 'paused'
  const isCompleted = agent.agentStatus === 'completed'
  const completedCount = agent.todos.filter((t) => t.status === 'completed').length
  const totalCount = agent.todos.filter((t) => t.status !== 'skipped').length
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  // All agent-CLI backends (Claude Code, Codex) are "agent" mode, not the
  // plain LLM API. Without this, a Codex run was mislabeled "LLM API" in the
  // header chip.
  const AGENT_MODE_LABELS: Record<string, string> = {
    claude_code: AGENT_DISPLAY_NAME,
    codex: AGENT_DISPLAY_NAME,
  }
  const isAgentMode = generationMode in AGENT_MODE_LABELS
  const modeLabel = AGENT_MODE_LABELS[generationMode] || 'LLM API'
  // `claudeModel` is always '' now — the server-level model setting is gone and
  // the model comes from per-task routing, reported per run by the task_info /
  // agent_config events. Show "Default" rather than naming a model the platform
  // never chose.
  const modelLabel = generationMode === 'claude_code'
    ? (claudeModel || 'Default')
    : isAgentMode
      ? 'Default'
      : languageModel || (llmProvider === 'openai' ? 'GPT' : llmProvider === 'google' ? 'Gemini' : llmProvider || 'Default')

  const sidebarWidth = compact ? 240 : 300

  return (
    <div className="auto-agent-page" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        flexShrink: 0,
        background: 'var(--color-bg)',
      }}>
        {showBackButton && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate(-1)}
            style={{ padding: '4px 8px' }}
          >
            <ArrowLeft size={16} />
          </button>
        )}
        <Bot size={18} style={{ color: 'var(--color-accent)' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '1rem' }}>AI Agent</span>
            <span style={{
              fontSize: '0.8125rem',
              padding: '1px 8px',
              borderRadius: 'var(--radius-full)',
              background: agent.wsStatus === 'connected'
                ? 'rgba(76,175,80,0.12)'
                : agent.wsStatus === 'connecting'
                ? 'rgba(255,152,0,0.12)'
                : 'rgba(158,158,158,0.12)',
              color: agent.wsStatus === 'connected'
                ? '#4caf50'
                : agent.wsStatus === 'connecting'
                ? '#ff9800'
                : 'var(--color-text-secondary)',
            }}>
              {agent.wsStatus === 'connected' ? 'Connected' : agent.wsStatus === 'connecting' ? 'Connecting…' : 'Disconnected'}
            </span>
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 1 }}>
            {project}
          </div>
        </div>

        {/* Controls */}
        <div className="auto-agent-controls" style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
          {!started && !isCompleted && (
            <>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleStart('step')}
                disabled={!description.trim() || agent.wsStatus !== 'connected'}
                title="Generate step by step: specification → review → application (analyses a referenced source first)"
              >
                <ListChecks size={14} />
                <span className="auto-agent-btn-label">Step by step</span>
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleStart('build')}
                disabled={!description.trim() || agent.wsStatus !== 'connected'}
                title="Build: the multi-agent Ask-AI orchestrator decides and runs every step"
              >
                <Sparkles size={14} />
                <span className="auto-agent-btn-label">Build</span>
              </button>
            </>
          )}
          {isRunning && (
            <button className="btn btn-secondary btn-sm" onClick={agent.pause}>
              <Pause size={14} />
              <span className="auto-agent-btn-label">Pause</span>
            </button>
          )}
          {isPaused && (
            <button className="btn btn-primary btn-sm" onClick={agent.resume}>
              <Play size={14} />
              <span className="auto-agent-btn-label">Resume</span>
            </button>
          )}
          {(isRunning || isPaused) && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={agent.cancel}
              style={{ color: 'var(--color-error, #cf222e)' }}
            >
              <StopCircle size={14} />
            </button>
          )}
          {(isCompleted || isPaused || agent.agentStatus === 'error') && (
            <button className="btn btn-ghost btn-sm" onClick={handleClearAndRestart}>
              <RotateCcw size={14} />
              <span className="auto-agent-btn-label">Restart</span>
            </button>
          )}
        </div>
      </div>

      {/* Info bar */}
      {started && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: `0.375rem 1rem 0.375rem ${showBackButton ? '3.25rem' : '1rem'}`,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}>
          <InfoChip label="Mode" value={modeLabel} />
          <InfoChip label="Model" value={agent.currentTask?.model || modelLabel} />
          {agent.currentTask ? (
            <>
              <InfoChip label="Task" value={agent.currentTask.task_type} />
              {agent.currentTask.effort && <InfoChip label="Effort" value={agent.currentTask.effort} />}
            </>
          ) : (
            thinkingEffort && <InfoChip label="Effort" value={thinkingEffort} />
          )}
          {totalCount > 0 && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>
              {completedCount}/{totalCount} steps
              {isCompleted && ' — Done'}
              {isPaused && ' — Paused'}
            </span>
          )}
        </div>
      )}

      {/* Progress bar */}
      {started && totalCount > 0 && (
        <div style={{ padding: '0 1rem', flexShrink: 0 }}>
          <div style={{ height: 3, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: isCompleted
                ? '#4caf50'
                : 'linear-gradient(90deg, #0061D0, #009974)',
              borderRadius: 'var(--radius-full)',
              transition: 'width 0.5s ease',
            }} />
          </div>
        </div>
      )}

      <div className="auto-agent-body" style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left: setup + TODO list */}
        <div className="auto-agent-sidebar" style={{
          width: sidebarWidth,
          flexShrink: 0,
          borderRight: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--color-bg)',
        }}>
          {showSetup && !started && (
            <div style={{ padding: '1rem', flexShrink: 0 }}>
              <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.375rem' }}>
                App Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the application you want to build..."
                style={{
                  width: '100%',
                  minHeight: 90,
                  padding: '0.5rem 0.625rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text)',
                  resize: 'vertical',
                  fontFamily: 'var(--font-primary)',
                }}
              />

              <div style={{ marginTop: '0.625rem' }}>
                <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.375rem' }}>
                  Deploy To (optional)
                </label>
                {loadingProfiles ? (
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Loading profiles…</div>
                ) : profiles.length === 0 ? (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6 }}>No deployment profiles configured</div>
                ) : (
                  <select
                    value={selectedProfileId ?? ''}
                    onChange={(e) => setSelectedProfileId(e.target.value ? Number(e.target.value) : null)}
                    style={{
                      width: '100%',
                      padding: '0.4rem 0.5rem',
                      fontSize: '0.875rem',
                      background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-text)',
                    }}
                  >
                    <option value="">— Skip deployment —</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.db_type})</option>
                    ))}
                  </select>
                )}
              </div>

              {agent.hasExistingState && (
                <div style={{
                  marginTop: '0.625rem',
                  padding: '0.5rem 0.625rem',
                  background: 'rgba(255,152,0,0.06)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                }}>
                  <AlertTriangle size={12} style={{ color: 'var(--color-warning, #9a6700)', flexShrink: 0 }} />
                  Previous run found — will resume where it left off.
                </div>
              )}
            </div>
          )}

          {/* TODO list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.375rem 0.5rem' }}>
            {agent.todos.length === 0 ? (
              <div style={{ padding: '1.5rem 0.75rem', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                {started ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    Building task list…
                  </div>
                ) : 'Tasks will appear here once generation starts.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                {agent.todos.map((todo) => (
                  <TodoItem key={todo.id} todo={todo} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: logs + chat */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.75rem 1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            minHeight: 0,
          }}>
            {agent.logs.length === 0 && agent.replies.length === 0 && !agent.question ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <Bot size={36} style={{ opacity: 0.15 }} />
                <p style={{ fontSize: '0.9375rem', textAlign: 'center', margin: 0, maxWidth: 340, lineHeight: 1.6 }}>
                  The AI Agent will generate your entire application — FRS, models, business logic, and deployment.
                  Choose <strong>Step by step</strong> to review each stage, or <strong>Build</strong> to let the
                  multi-agent orchestrator run end to end (it can build from a Google Sheet / database link too).
                </p>
                {!started && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleStart('step')}
                      disabled={!description.trim() || agent.wsStatus !== 'connected'}
                    >
                      <ListChecks size={16} />
                      Step by step
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleStart('build')}
                      disabled={!description.trim() || agent.wsStatus !== 'connected'}
                    >
                      <Sparkles size={16} />
                      Build
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {[...agent.logs.map(l => ({ kind: 'log' as const, item: l })),
                  ...agent.replies.map(r => ({ kind: 'reply' as const, item: r }))]
                  .sort((a, b) => a.item.timestamp.getTime() - b.item.timestamp.getTime())
                  .map(({ kind, item }) => {
                    if (kind === 'log') {
                      const log = item as typeof agent.logs[0]
                      return (
                        <div key={`log-${log.id}`} style={{
                          display: 'flex',
                          gap: '0.5rem',
                          alignItems: 'flex-start',
                          fontSize: '0.875rem',
                          lineHeight: 1.5,
                        }}>
                          <span style={{
                            color: log.level === 'error' ? '#f44336' : log.level === 'warning' ? '#ff9800' : log.level === 'success' ? '#4caf50' : 'var(--color-text-secondary)',
                            marginTop: 2,
                            flexShrink: 0,
                            fontSize: '0.8125rem',
                            fontFamily: 'monospace',
                          }}>
                            {log.level === 'error' ? '✗' : log.level === 'warning' ? '⚠' : log.level === 'success' ? '✓' : '›'}
                          </span>
                          <span style={{
                            flex: 1,
                            minWidth: 0,
                            color: log.level === 'error' ? '#f44336' : log.level === 'warning' ? '#ff9800' : 'var(--color-text)',
                          }}>
                            {log.message}
                          </span>
                          {(log.elapsedS != null || log.tokens != null || log.costUsd != null) && (
                            <span style={{
                              flexShrink: 0, marginLeft: 'auto', whiteSpace: 'nowrap',
                              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                              opacity: 0.8, marginTop: 2,
                            }}>
                              {[
                                log.elapsedS != null ? formatDuration(log.elapsedS) : null,
                                log.tokens != null
                                  ? (log.tokens >= 1000
                                    ? `${(log.tokens / 1000).toFixed(1)}k tokens`
                                    : `${log.tokens} tokens`)
                                  : null,
                                formatSpend(log.costUsd),
                              ].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </div>
                      )
                    } else {
                      const reply = item as typeof agent.replies[0]
                      return (
                        <div key={`reply-${reply.id}`} style={{
                          display: 'flex',
                          justifyContent: reply.role === 'user' ? 'flex-end' : 'flex-start',
                          marginTop: '0.375rem',
                        }}>
                          <div style={{
                            maxWidth: '85%',
                            padding: '0.5rem 0.75rem',
                            borderRadius: reply.role === 'user'
                              ? 'var(--radius-lg) var(--radius-lg) 4px var(--radius-lg)'
                              : 'var(--radius-lg) var(--radius-lg) var(--radius-lg) 4px',
                            background: reply.role === 'user'
                              ? 'var(--color-accent)'
                              : 'var(--color-bg-secondary)',
                            color: reply.role === 'user' ? '#fff' : 'var(--color-text)',
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                          }}>
                            {reply.role === 'agent' ? (
                              <div className="markdown-content">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{reply.content}</ReactMarkdown>
                              </div>
                            ) : reply.content}
                          </div>
                        </div>
                      )
                    }
                  })}

                {/* Active change step output */}
                {agent.activeChangeStep && agent.streamingStepOutput && (
                  <div style={{
                    margin: '0.5rem 0',
                    padding: '0.625rem 0.75rem',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    maxHeight: 200,
                    overflowY: 'auto',
                    borderLeft: '3px solid var(--color-accent)',
                    opacity: 0.85,
                  }}>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-accent)', marginBottom: 4, fontWeight: 600 }}>
                      Agent Output
                    </div>
                    {agent.streamingStepOutput}
                  </div>
                )}

                {/* Scope-confirmation round (pre-FRS build) — rich selectable
                    questions instead of the plain option bubble. */}
                {agent.question && agent.question.scope && (
                  <div style={{ marginTop: '0.375rem' }}>
                    <ScopeConfirmation
                      proposal={agent.question.scope}
                      compact
                      confirmLabel="Confirm & build"
                      onConfirm={(decisions) =>
                        agent.answerScopeQuestion(agent.question!.question_id, decisions)}
                      onSkip={() => handleAnswerQuestion('')}
                    />
                  </div>
                )}

                {/* Large-source scoping round — pick seed tables; related
                    tables are pulled in automatically before exploration
                    unless the user turns the expansion toggle off. */}
                {agent.question && agent.question.table_picker && (
                  <div style={{ marginTop: '0.375rem' }}>
                    <SourceTablePicker
                      payload={agent.question.table_picker}
                      question={agent.question.question}
                      compact
                      onConfirm={(tables, includeRelated) =>
                        agent.answerTablePicker(
                          agent.question!.question_id, tables, includeRelated)}
                      onSkip={() =>
                        agent.answerTablePicker(agent.question!.question_id, [])}
                    />
                  </div>
                )}

                {/* Question bubble — the shared select-or-type bubble the
                    Ask-AI docks use, including the connection-definition
                    form when the agent flags a connection problem. */}
                {agent.question && !agent.question.scope && !agent.question.table_picker && (
                  <AgentAskBubble
                    question={agent.question.question}
                    options={agent.question.options}
                    onAnswer={handleAnswerQuestion}
                    connectionForm={!!agent.question.connection_form}
                    project={project}
                  />
                )}

                {/* Phase review — the agent paused at a phase boundary and is
                    waiting for the user's decision. Drives the same
                    continue / task-by-task / complete scopes the dock uses. */}
                {agent.awaitingReview && !agent.question && (
                  <div style={{
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '0.875rem',
                    marginTop: '0.375rem',
                    border: '1px solid var(--color-accent)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
                      <ListChecks size={13} style={{ color: 'var(--color-accent)' }} />
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                        Paused for your review
                      </span>
                    </div>
                    <p style={{ fontSize: '0.875rem', margin: '0 0 0.625rem', lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
                      Review the plan{agent.phases.length > 0 ? ` (${agent.phases.length} phase${agent.phases.length === 1 ? '' : 's'})` : ''}, then choose how to proceed.
                    </p>
                    <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={agent.continueNextPhase}
                        title="Build the next phase, then pause again for review"
                      >
                        <ChevronRight size={12} />
                        Continue (next phase)
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={agent.buildTaskByTask}
                        title="Build just the next object, then pause again"
                      >
                        <SkipForward size={12} />
                        Task by task
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={agent.completeEverything}
                        title="Build everything remaining without pausing"
                      >
                        <CheckCircle2 size={12} />
                        Complete everything
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={logEndRef} />
          </div>

          {/* Chat input */}
          <div style={{
            padding: '0.625rem 1rem',
            display: 'flex',
            gap: '0.375rem',
            alignItems: 'flex-end',
            flexShrink: 0,
            background: 'var(--color-bg)',
          }}>
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask the agent to make changes…"
              rows={2}
              style={{
                flex: 1,
                padding: '0.5rem 0.625rem',
                fontSize: '0.875rem',
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
                resize: 'none',
                fontFamily: 'var(--font-primary)',
                lineHeight: 1.5,
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage()
                }
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {isAgentMode && (
                <select
                  value={chatModel}
                  onChange={(e) => setChatModel(e.target.value)}
                  title="Model override for this message"
                  style={{
                    padding: '0.25rem 0.3rem',
                    fontSize: '0.8125rem',
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    minWidth: 0,
                    width: '100%',
                  }}
                >
                  {/* Model catalog is per-agent (Claude Code / Codex / Gemini)
                      from the single-source llmCatalog, so Codex shows GPT
                      models rather than the previously hardcoded Claude list. */}
                  <option value="">Default</option>
                  {modelsForAgent(generationMode as AgentId).map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              )}
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSendMessage}
                disabled={!chatInput.trim()}
                style={{ padding: '0.4rem' }}
              >
                <Send size={14} />
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={agent.clearLogs}
                title="Clear chat"
                style={{ padding: '0.4rem' }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
